import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Radar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { devicesService } from '../../services/devices';
import type { BluetoothScanResult } from '../../types/device';

interface BoardBluetoothScannerProps {
  gymSlug: string;
  /** The device that does the sweeping — the one driving this board. */
  deviceId: string;
  /** MAC currently on the form, so the picker reflects what is saved. */
  selectedAddress: string;
  onSelect: (result: BluetoothScanResult) => void;
}

/**
 * Picks a board's Bluetooth identity off the air instead of by hand: "Scan"
 * asks the gym's device to sweep, and what it hears fills the name and MAC.
 *
 * The device answers asynchronously and in instalments — it reports what it has
 * heard every few seconds while it listens — so this polls at the cadence the
 * server asks for and shows the list as it grows, pickable before the sweep is
 * over. Nothing here is saved: picking an entry only fills the form fields.
 */
export default function BoardBluetoothScanner({
  gymSlug,
  deviceId,
  selectedAddress,
  onSelect,
}: BoardBluetoothScannerProps) {
  const { t } = useTranslation();
  const [results, setResults] = useState<BluetoothScanResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [reported, setReported] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const scan = await devicesService.getBluetoothScan(gymSlug, deviceId);
      if (!mountedRef.current) return;

      setResults(scan.results);
      setScanning(scan.scanning);
      setReported(scan.results_at !== null);
      // Every report carries the whole sweep, so replacing is enough — and the
      // server's own flag, which the device closes, is the only stop condition.
      if (!scan.scanning) stopPolling();
    } catch {
      // A dropped poll is not worth interrupting the scan for — the next tick
      // asks again, and the scan ends on its own either way.
    }
  }, [gymSlug, deviceId, stopPolling]);

  const startPolling = useCallback(
    (intervalSeconds: number) => {
      stopPolling();
      timerRef.current = setInterval(poll, Math.max(1, intervalSeconds) * 1000);
    },
    [poll, stopPolling]
  );

  // A scan may already be running or freshly finished when the form opens (the
  // admin reopened it, or switched device and back), so pick up where it is.
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    setResults([]);
    setScanning(false);
    setReported(false);
    setError(null);

    devicesService
      .getBluetoothScan(gymSlug, deviceId)
      .then((scan) => {
        if (cancelled) return;
        setResults(scan.results);
        setScanning(scan.scanning);
        setReported(scan.results_at !== null);
        if (scan.scanning) startPolling(scan.poll_interval);
      })
      .catch(() => {
        // Nothing to show yet is the normal state; the Scan button still works.
      });

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [gymSlug, deviceId, startPolling, stopPolling]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      stopPolling();
    },
    [stopPolling]
  );

  const handleScan = async () => {
    setError(null);
    setStarting(true);
    try {
      const scan = await devicesService.startBluetoothScan(gymSlug, deviceId);
      if (!mountedRef.current) return;
      setResults(scan.results);
      setScanning(scan.scanning);
      setReported(scan.results_at !== null);
      if (scan.scanning) startPolling(scan.poll_interval);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : t('errors.unexpectedError'));
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  };

  const handleSelect = (address: string) => {
    const result = results.find((item) => item.address === address);
    if (result) onSelect(result);
  };

  const busy = starting || scanning;

  const status = () => {
    if (error) return error;
    // A sweep still running may already have heard something: say how much, so
    // the list filling up in the dropdown is explained rather than surprising.
    if (busy && results.length > 0)
      return t('gymAdmin.boards.scan.scanningFound', { count: results.length });
    if (busy) return t('gymAdmin.boards.scan.scanning');
    if (reported && results.length === 0) return t('gymAdmin.boards.scan.noResults');
    if (results.length > 0) return t('gymAdmin.boards.scan.found', { count: results.length });
    return t('gymAdmin.boards.scan.hint');
  };

  return (
    <div className="space-y-2">
      <Label>{t('gymAdmin.boards.scan.label')}</Label>
      <div className="flex items-center gap-2">
        <Select
          value={selectedAddress}
          onValueChange={handleSelect}
          disabled={results.length === 0}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={t('gymAdmin.boards.scan.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {results.map((result) => (
              <SelectItem key={result.address} value={result.address}>
                <span className="mr-2">{result.name || t('gymAdmin.boards.scan.unnamed')}</span>
                <span className="font-mono text-xs text-muted-foreground">{result.address}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" onClick={handleScan} disabled={busy}>
          {busy ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Radar className="mr-2 size-4" />
          )}
          {t('gymAdmin.boards.scan.scanButton')}
        </Button>
      </div>
      <p className={`text-xs ${error ? 'text-destructive' : 'text-muted-foreground'}`}>{status()}</p>
    </div>
  );
}
